import React from "react";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai"; // Added AiFillHeart for filled state
import { Product, ProductCollection } from "../types";

interface ProductCardProps {
  product: Product | ProductCollection;
  handleNavigate: (url: string) => void;
  isWishlisted: boolean;
  onToggleWishlist: (product: Product) => void;
}

const calculateTotalQuantity = (product: ProductCollection): number => {
  if (product.sizes && Array.isArray(product.sizes)) {
    return product.sizes.reduce((total, { quantity }) => total + (quantity || 0), 0);
  }
  return 10; // Default fallback as per original code
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  handleNavigate,
  isWishlisted,
  onToggleWishlist,
}) => {
  const totalQuantity = calculateTotalQuantity(product as ProductCollection);
  let stockMessage = "";
  let disableButton = false;

  if (totalQuantity === 0) {
    stockMessage = "OUT OF STOCK";
    disableButton = true;
  } else if (totalQuantity <= 5) {
    stockMessage = `ONLY ${totalQuantity} LEFT`;
  }

  // Cast for price access if strictly needed, though interface usually has it
  const price = (product as any).price;

  return (
    <div
      className="group relative flex flex-col w-full bg-transparent cursor-pointer"
      onClick={() => handleNavigate(`${product.id}`)}
    >
      {/* Image Container - Aspect 3:4 for portrait fashion feel */}
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-gray-100 mb-4">
        <img
          className={`w-full h-full object-cover object-center transition-transform duration-700 ease-in-out group-hover:scale-105 ${totalQuantity === 0 ? "opacity-60" : ""
            }`}
          src={product.image || "https://via.placeholder.com/300"}
          alt={product.name}
        />

        {/* Stock Badge - Minimalist */}
        {stockMessage && (
          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1">
            <span className="text-[10px] font-bold tracking-wider text-red-600 uppercase">
              {stockMessage}
            </span>
          </div>
        )}

        {/* Wishlist Button - Top right, subtle */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist(product as Product);
          }}
          disabled={disableButton}
          className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${isWishlisted
              ? "bg-red-500 text-white shadow-md"
              : "bg-white/80 backdrop-blur-sm text-black hover:bg-black hover:text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0"
            }`}
          title="Add to Wishlist"
        >
          {isWishlisted ? <AiFillHeart size={14} /> : <AiOutlineHeart size={16} />}
        </button>
      </div>

      {/* Product Info */}
      <div className="flex flex-col items-center space-y-1 px-1">
        <h3 className="text-sm font-medium tracking-widest text-[#1B1B1B] uppercase text-center group-hover:text-gray-600 transition-colors">
          {product.name}
        </h3>

        {/* Price Display if available */}
        {price !== undefined && (
          <p className="text-sm font-semibold text-gray-900">
            ₹{price.toLocaleString('en-IN')}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
